import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles, Users, Building2, Rocket, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import CurrencyToggle from "@/components/CurrencyToggle";
import SiteHeader from "@/components/SiteHeader";
import AppFooter from "@/components/AppFooter";
import { useCurrencyRates, convertAmount } from "@/lib/currency";
import { CURRENCY_SYMBOLS } from "@/lib/format";
import { createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

/**
 * Pricing canonical: EUR. Converted amounts to USD/NOK are labelled approximate
 * with ECB rate + date. Monthly/annual toggle. VAT note on pricing display.
 */

const PLANS = [
  {
    key: "free",
    name: "Free",
    priceEUR: { month: 0, year: 0 },
    tagline: "Your first campaign, on us",
    icon: Sparkles,
    accent: "#94A3B8",
    features: [
      "One active calculation",
      "Basic scorecard",
      "Base-case scenario only",
      "Email-delivered result",
      "Branded report preview",
      "No PDF, saved reports or comparison view",
    ],
    cta: { label: "Start free", to: "/calculator" },
    featured: false,
  },
  {
    key: "pro",
    name: "Pro",
    priceEUR: { month: 29, year: 290 },
    tagline: "For CMOs, founders and small teams",
    icon: Rocket,
    accent: "#FFC53D",
    lookupPrefix: "pro",
    features: [
      "Unlimited calculations",
      "Conservative, base and optimistic scenarios",
      "Full PDF export",
      "Scorecard + finance-detail report layers",
      "Saved report history",
      "Nordic benchmarks + global fallback",
      "Source and date labels on every input",
      "Scenario comparison view",
      "NOK / EUR / USD support",
    ],
    cta: { label: "Choose Pro", to: "/calculator" },
    featured: true,
  },
  {
    key: "team",
    name: "Team",
    priceEUR: { month: 79, year: 790 },
    tagline: "For comms teams and in-house PR",
    icon: Users,
    accent: "#10B981",
    lookupPrefix: "team",
    features: [
      "Everything in Pro",
      "Up to five users",
      "Shared workspace",
      "Client & project folders",
      "Report duplication",
      "Team notes & comments",
      "Custom benchmark inputs",
      "Priority support",
      "Optional custom logo on reports",
    ],
    cta: { label: "Choose Team", to: "/calculator" },
    featured: false,
  },
  {
    key: "agency",
    name: "Agency",
    priceEUR: { month: 199, year: 1990 },
    tagline: "For agencies serving multiple clients",
    icon: Building2,
    accent: "#0055FF",
    features: [
      "Everything in Team",
      "Multiple client workspaces",
      "White-label PDF reports",
      "Custom domain",
      "Agency logo & colours",
      "Custom methodology notes",
      "Benchmark administration",
      "Multi-seat & commercial reporting rights",
      "Priority onboarding",
    ],
    cta: { label: "Contact sales", href: "mailto:sales@nordicmartech.com?subject=PressImpact%20Agency%20plan" },
    featured: false,
    priceSuffix: "from",
  },
];

function displayPrice(eurValue, targetCurrency, rates, cycle) {
  if (eurValue === 0) return { main: "€0", sub: cycle === "year" ? "per year" : "per month" };
  const converted = targetCurrency === "EUR" ? eurValue : convertAmount(eurValue, "EUR", targetCurrency, rates);
  const sym = CURRENCY_SYMBOLS[targetCurrency];
  const rounded = Math.round(converted);
  const formatted = targetCurrency === "NOK" ? `${rounded.toLocaleString()}\u00a0${sym}` : `${sym}${rounded.toLocaleString()}`;
  return { main: formatted, sub: cycle === "year" ? "per year" : "per month" };
}

export default function Pricing() {
  const [currency, setCurrency] = useState("EUR");
  const [cycle, setCycle] = useState("month"); // "month" | "year"
  const [loadingKey, setLoadingKey] = useState(null);
  const { rates, asOf, source, formatDate } = useCurrencyRates();

  const monthEquivalent = useMemo(
    () => (eurValue) => (cycle === "year" ? Math.round((eurValue / 12) * 100) / 100 : eurValue),
    [cycle],
  );

  async function startCheckout(plan) {
    if (!plan.lookupPrefix) return;
    const lookup_key = `${plan.lookupPrefix}_${cycle === "year" ? "yearly" : "monthly"}`;
    setLoadingKey(plan.key);
    try {
      const { checkout_url } = await createCheckoutSession({
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
      toast.error("Could not start checkout. Please try again.");
      setLoadingKey(null);
    }
  }

  return (
    <div className="min-h-screen bg-[#FFFDF6] text-slate-900">
      <SiteHeader />

      <main className="max-w-6xl mx-auto px-5 md:px-8 lg:px-10 py-12 md:py-16">
        {/* Hero */}
        <section className="mb-10 md:mb-12 text-center">
          <p className="text-xs tracking-[0.32em] uppercase text-orange-600 font-semibold mb-4">
            Pricing
          </p>
          <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Priced for individual clarity,{" "}
            <span className="relative inline-block">
              <span className="relative z-10">not enterprise ceremony.</span>
              <span className="absolute left-0 right-0 bottom-1 h-2.5 md:h-3 bg-[#FFC53D]/70 -z-0" aria-hidden="true" />
            </span>
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-[1.55]">
            Free gives you the scorecard. Paid unlocks the boardroom-ready report,
            saved history, comparison view, and — on the top tiers — white-label and
            client workspaces.
          </p>

          {/* Cycle + Currency — grouped, centred, responsive */}
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <div className="inline-flex items-center bg-white border border-slate-200 rounded-sm p-0.5">
              {[
                ["month", "Monthly"],
                ["year", "Annual · save ~17%"],
              ].map(([value, label]) => {
                const active = cycle === value;
                return (
                  <button
                    key={value}
                    data-testid={`cycle-${value}`}
                    onClick={() => setCycle(value)}
                    className={`px-4 sm:px-5 py-2 text-xs sm:text-sm font-medium tracking-wide rounded-sm transition-colors ${
                      active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <CurrencyToggle
              value={currency}
              onChange={setCurrency}
              rates={rates}
              asOf={asOf}
              source={source}
              formatDate={formatDate}
            />
          </div>
        </section>

        {/* Plan grid */}
        <section
          data-testid="pricing-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {PLANS.map((plan) => {
            const eur = plan.priceEUR[cycle];
            const { main, sub } = displayPrice(eur, currency, rates, cycle);
            const monthlyEquivalent = cycle === "year" && eur > 0
              ? displayPrice(monthEquivalent(eur), currency, rates, "month").main
              : null;
            const Icon = plan.icon;
            return (
              <div
                key={plan.key}
                data-testid={`plan-${plan.key}`}
                className={`relative flex flex-col border p-6 md:p-7 bg-white transition-all duration-300 ${
                  plan.featured
                    ? "border-slate-900 shadow-[6px_6px_0_0_#FFC53D] hover:shadow-[8px_8px_0_0_#FFC53D] hover:-translate-y-0.5"
                    : "border-slate-200 hover:border-slate-900 hover:shadow-[4px_4px_0_0_#0A0A0A] hover:-translate-y-0.5"
                }`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-6 bg-[#FFC53D] text-slate-900 text-[10px] tracking-[0.24em] uppercase font-semibold px-2 py-1">
                    Most popular
                  </span>
                )}

                <div className="flex items-center justify-between mb-6">
                  <div
                    className="h-10 w-10 grid place-items-center"
                    style={{ background: `${plan.accent}22` }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.6} style={{ color: plan.accent }} />
                  </div>
                  <span className="mono text-[10px] tracking-[0.2em] text-slate-400">
                    {plan.name.toUpperCase()}
                  </span>
                </div>

                <h3 className="font-heading text-2xl font-medium text-slate-900 mb-1">
                  {plan.name}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-6 min-h-[36px]">
                  {plan.tagline}
                </p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    {plan.priceSuffix && (
                      <span className="text-xs text-slate-400 uppercase tracking-widest">
                        {plan.priceSuffix}
                      </span>
                    )}
                    <span className="font-heading text-4xl font-light tracking-tight text-slate-900">
                      {main}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{sub}</div>
                  {monthlyEquivalent && (
                    <div className="text-[10px] text-slate-400 mt-1 mono">
                      ≈ {monthlyEquivalent}/mo
                    </div>
                  )}
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700 leading-snug">
                      <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>

                {plan.lookupPrefix ? (
                  <Button
                    data-testid={`plan-cta-${plan.key}`}
                    disabled={loadingKey === plan.key}
                    onClick={() => startCheckout(plan)}
                    className={`w-full rounded-sm ${
                      plan.featured
                        ? "bg-slate-900 hover:bg-slate-800 text-white"
                        : "bg-white hover:bg-slate-900 hover:text-white border border-slate-900 text-slate-900"
                    }`}
                  >
                    {loadingKey === plan.key ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      <>
                        {plan.cta.label}
                        <ArrowUpRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                ) : plan.cta.to ? (
                  <Link to={plan.cta.to}>
                    <Button
                      data-testid={`plan-cta-${plan.key}`}
                      className={`w-full rounded-sm ${
                        plan.featured
                          ? "bg-slate-900 hover:bg-slate-800 text-white"
                          : "bg-white hover:bg-slate-900 hover:text-white border border-slate-900 text-slate-900"
                      }`}
                    >
                      {plan.cta.label}
                      <ArrowUpRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <a href={plan.cta.href}>
                    <Button
                      data-testid={`plan-cta-${plan.key}`}
                      className="w-full bg-white hover:bg-slate-900 hover:text-white border border-slate-900 text-slate-900 rounded-sm"
                    >
                      {plan.cta.label}
                      <ArrowUpRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                )}
              </div>
            );
          })}
        </section>

        {/* VAT + currency note */}
        <section className="mt-12 border border-slate-200 bg-white p-6 text-sm text-slate-600 leading-relaxed" data-testid="pricing-notes">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500 font-semibold mb-3">
            Pricing notes
          </p>
          <ul className="space-y-2">
            <li>
              Prices are canonical in <strong>EUR</strong>. Displayed amounts in NOK and USD
              are approximate, converted using European Central Bank reference rates
              {asOf ? ` on ${formatDate(asOf)}` : ""}. Final payment currency is confirmed at checkout.
            </li>
            <li>
              Prices are shown <strong>excluding VAT</strong>. VAT treatment depends on your
              billing country and is displayed on the invoice.
            </li>
            <li>
              Annual plans are billed once yearly and offer approximately two months free
              versus month-to-month.
            </li>
            <li>
              The <strong>free tier</strong> is genuinely usable — no credit card, no time limit.
              Upgrade is only required when you need PDF export, saved reports, or multi-user access.
            </li>
          </ul>
        </section>

        {/* Premium gate explanation */}
        <section className="mt-8 border border-slate-900 bg-slate-900 text-white p-8">
          <p className="text-xs tracking-[0.28em] uppercase text-[#FFC53D] font-semibold mb-3">
            What you unlock with Pro
          </p>
          <h2 className="font-heading text-2xl md:text-3xl font-light tracking-tight mb-4">
            The full boardroom-ready report.
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-2xl">
            CMO scorecard, CFO detail with methodology, three-scenario sensitivity,
            all assumptions with sources and dates, currency footer with ECB provenance,
            and a shareable PDF. Everything a stakeholder needs to sign off — nothing
            hidden behind separate add-ons.
          </p>
          <div className="mt-6">
            <Link to="/calculator">
              <Button
                data-testid="premium-gate-cta"
                size="lg"
                className="bg-[#FFC53D] hover:bg-[#FFD866] text-slate-900 rounded-sm px-6 py-5 font-semibold"
              >
                Run your first analysis
                <ArrowUpRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
